import API from './apiAxios';

export interface ValidationRequestItem {
  field: string;
  value: any;
  type: string;
  resolve: (value: ValidationResult) => void;
  reject: (reason: any) => void;
}

export interface ValidationResult {
  field: string;
  type: string;
  valid: boolean | null;
  suggestion: string | null;
  isStale?: boolean;
  error?: string;
}

class ValidationService {
  private queue: ValidationRequestItem[] = [];
  private flushTimeout: any = null;

  /**
   * Queues a field for validation and returns a promise resolving to its validation result.
   */
  public validate(field: string, type: string, value: any): Promise<ValidationResult> {
    return new Promise<ValidationResult>((resolve, reject) => {
      // Add to queue
      this.queue.push({ field, type, value, resolve, reject });

      // Schedule flush on the next tick/microtask
      if (!this.flushTimeout) {
        this.flushTimeout = setTimeout(() => this.flush(), 0);
      }
    });
  }

  private async flush() {
    this.flushTimeout = null;
    const currentQueue = [...this.queue];
    this.queue = [];

    if (currentQueue.length === 0) return;

    // Filter duplicates within the same batch to avoid redundant API calls
    const latestItemsMap = new Map<string, ValidationRequestItem>();

    currentQueue.forEach(item => {
      const existing = latestItemsMap.get(item.field);
      if (existing) {
        // Resolve the older duplicate request with isStale: true
        existing.resolve({
          field: existing.field,
          type: existing.type,
          valid: null,
          suggestion: null,
          isStale: true,
        });
      }
      latestItemsMap.set(item.field, item);
    });

    const itemsToSend = Array.from(latestItemsMap.values());

    try {
      const payload = {
        items: itemsToSend.map(item => ({
          field: item.field,
          value: item.value,
          type: item.type,
        })),
      };

      // Call the AI validation API
      const response = await API.post('/speedboxbot/validate', payload);
      const results = response.data?.results || [];

      // Map results by field for quick lookup
      const resultsMap = new Map<string, any>();
      results.forEach((res: any) => {
        resultsMap.set(res.field, res);
      });

      // Resolve the promises for the batch
      itemsToSend.forEach(item => {
        const res = resultsMap.get(item.field);
        if (res) {
          item.resolve({
            field: res.field,
            type: res.type,
            valid: res.valid,
            suggestion: res.suggestion,
          });
        } else {
          item.resolve({
            field: item.field,
            type: item.type,
            valid: false,
            suggestion: null,
            error: 'No result found in API response',
          });
        }
      });
    } catch (error: any) {
      console.error('Validation API Error:', error);
      // Reject the pending items in the current batch
      itemsToSend.forEach(item => {
        item.reject(error);
      });
    }
  }
}

export const validationService = new ValidationService();
export default validationService;
